'use client'

import { useState } from 'react'
import { Check, Copy, ArrowUpRight } from 'lucide-react'
import { trackModelShowcaseInteraction } from '@/lib/analytics'

const LLMS_URL = '/models/flux-3/reports/llms.txt'

/**
 * The whole write-up as one plain-text file, following the llms.txt convention.
 * Reading thirteen reports to answer one question about FLUX.3 is a job for a
 * model, so the primary affordance is "put it on my clipboard" rather than
 * "open a wall of text" — though the link is there for anyone who wants it.
 */
export function Flux3LlmsCopy() {
  const [state, setState] = useState<'idle' | 'copying' | 'done' | 'failed'>('idle')

  const copy = async () => {
    setState('copying')
    try {
      const res = await fetch(LLMS_URL)
      if (!res.ok) throw new Error(String(res.status))
      await navigator.clipboard.writeText(await res.text())
      trackModelShowcaseInteraction({ model: 'flux-3', interaction: 'llms_copied' })
      setState('done')
      setTimeout(() => setState('idle'), 2400)
    } catch {
      setState('failed')
      setTimeout(() => setState('idle'), 2400)
    }
  }

  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
      <button
        type="button"
        onClick={copy}
        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
      >
        {state === 'done' ? (
          <Check strokeWidth={2} className="h-4 w-4" />
        ) : (
          <Copy strokeWidth={1.75} className="h-4 w-4" />
        )}
        {state === 'done'
          ? 'Copied — paste into any model'
          : state === 'failed'
            ? "Couldn't copy — open it instead"
            : state === 'copying'
              ? 'Copying…'
              : 'Copy the whole write-up for an LLM'}
      </button>

      <a
        href={LLMS_URL}
        className="group inline-flex items-center gap-1.5 font-mono text-xs text-white/40 transition-colors hover:text-white/80"
      >
        llms.txt
        <ArrowUpRight
          strokeWidth={1.75}
          className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </a>
    </div>
  )
}
